"use client";

import Link from "next/link";
import { Send } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import type { Locale } from "@/lib/i18n";

/**
 * Where a project is handed in (`LN-ASG02`, docs/35 §5).
 *
 * The reader shows the brief; the submission is the assignment workspace's
 * (docs/26 §7.4), so this is a link and nothing else. It is drawn only when the
 * server sent `lesson.project`, which it does for a published, lesson-linked
 * assignment the workspace would open for this reader; a draft or a refused
 * reader gets no card and no word about submitting.
 */
export function LearnProjectSubmit({
  assignmentId,
  locale,
}: {
  assignmentId: string;
  locale: Locale;
}) {
  const t = useTranslations("learn.reader");
  return (
    <section
      aria-labelledby="learn-project-submit"
      className="mt-12 rounded-[var(--radius)] border border-neutral-200 bg-white p-5"
    >
      <h2
        className="flex items-center gap-2 text-sm font-semibold text-neutral-900"
        id="learn-project-submit"
      >
        <Send aria-hidden className="h-4 w-4 text-primary" />
        {t("projectSubmitHeading")}
      </h2>
      <p className="mt-1.5 text-sm text-neutral-600">{t("projectSubmitBody")}</p>
      <Button asChild className="mt-4">
        <Link href={`/${locale}/dashboard/assignments/${encodeURIComponent(assignmentId)}`}>
          {t("projectSubmitCta")}
        </Link>
      </Button>
    </section>
  );
}
